/**
 * Dynamic Sidebar Component
 * 
 * Displays navigation categories with progress indicators.
 * Categories and subcategories are derived from screen data.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';

// Subcategory structure
export interface SubcategoryItem {
  id: string;
  name: string;
  screenId: string;
  isComplete: boolean;
  isActive: boolean;
}

// Category structure matching Figma design
export interface SidebarCategory {
  id: string;
  name: string;
  icon: string;
  subcategories: SubcategoryItem[];
  screensTotal: number;
  screensCompleted: number;
  isActive: boolean;
  isAccessible: boolean;
}

interface SidebarProps {
  categories: SidebarCategory[];
  /** Resolved sidebar section id (e.g. `my-information`), not raw API `screen.category`. */
  currentCategory: string;
  currentScreenId?: string;
  onCategoryClick?: (categoryId: string) => void;
  onSubcategoryClick?: (screenId: string) => void;
}

/** Maps FIGMA/API `screen.category` strings into seven sidebar buckets (first match wins). */
export interface SidebarSectionConfig {
  id: string;
  label: string;
  categoryMatchers: string[];
}

export const SIDEBAR_SECTIONS: SidebarSectionConfig[] = [
  { id: 'start', label: 'Start', categoryMatchers: ['onboarding', 'getting started', 'getting-started', 'other'] },
  {
    id: 'my-information',
    label: 'My Information',
    categoryMatchers: ['personal-info', 'personal info', 'personal', 'family', 'residency', 'my info'],
  },
  { id: 'income', label: 'Income', categoryMatchers: ['income', 'housing'] },
  { id: 'deductions', label: 'Deductions', categoryMatchers: ['deduction'] },
  { id: 'foreign-reporting', label: 'Foreign Reporting', categoryMatchers: ['foreign'] },
  { id: 'summary', label: 'Summary', categoryMatchers: ['summary', 'review'] },
  { id: 'submission', label: 'Submission', categoryMatchers: ['submission', 'payment'] },
];

const SECTION_ICONS: Record<string, string> = {
  start: '🚀',
  'my-information': '👤',
  income: '💰',
  deductions: '💸',
  'foreign-reporting': '🌍',
  summary: '✅',
  submission: '📤',
};

const SIDEBAR_SECTION_ORDER = SIDEBAR_SECTIONS.map((s) => s.id);

function normalizeForMatch(s: string): string {
  return s.toLowerCase().trim().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ');
}

/** True if API category string satisfies a matcher (exact or substring, e.g. deductions ↔ deduction). */
function categoryMatchesMatcher(rawCategory: string, matcher: string): boolean {
  const c = normalizeForMatch(rawCategory);
  const m = normalizeForMatch(matcher);
  if (!c || !m) return false;
  if (c === m) return true;
  if (c.includes(m) || m.includes(c)) return true;
  return false;
}

/**
 * Which sidebar section a screen belongs to. Iterates `SIDEBAR_SECTIONS` in order; first matching matcher wins.
 * Unmapped categories default to `start` (extend `categoryMatchers` if too many screens pile up under Start).
 */
export function resolveSectionIdForCategory(rawCategory: string): string {
  const cat = rawCategory?.trim() || 'other';
  for (const sec of SIDEBAR_SECTIONS) {
    for (const m of sec.categoryMatchers) {
      if (categoryMatchesMatcher(cat, m)) {
        return sec.id;
      }
    }
  }
  return 'start';
}

export const Sidebar: React.FC<SidebarProps> = ({
  categories,
  currentCategory,
  currentScreenId,
  onCategoryClick,
  onSubcategoryClick
}) => {
  const { t } = useTranslation(['common', 'screens']);
  // Filter to only show section rows that have screens (order follows SIDEBAR_SECTIONS)
  const mainCategories = SIDEBAR_SECTION_ORDER.map((sectionId) => categories.find((c) => c.id === sectionId))
    .filter((c): c is SidebarCategory => c !== undefined && c.screensTotal > 0);

  // Track expanded state for each category - auto-expand current category
  const [expandedCategories, setExpandedCategories] = React.useState<Set<string>>(
    new Set([currentCategory])
  );

  // Auto-expand current category when it changes
  React.useEffect(() => {
    if (currentCategory) {
      setExpandedCategories(prev => {
        const next = new Set(prev);
        next.add(currentCategory);
        return next;
      });
    }
  }, [currentCategory]);

  // Also expand if current screen is in a category's subcategories
  React.useEffect(() => {
    if (currentScreenId) {
      const categoryWithScreen = categories.find(cat =>
        cat.subcategories.some(sub => sub.screenId === currentScreenId)
      );
      if (categoryWithScreen) {
        setExpandedCategories(prev => {
          const next = new Set(prev);
          next.add(categoryWithScreen.id);
          return next;
        });
      }
    }
  }, [currentScreenId, categories]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  return (
    <aside className="w-64 shrink-0 bg-white border-r border-gray-200 flex flex-col min-h-0 overflow-hidden">
      {/* Logo */}
      <div className="px-4 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-xl">💡</span>
          <span className="text-lg font-bold text-gray-900">InnoTaxes</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 min-h-0 overflow-y-auto py-4">
        <ul className="space-y-1 px-3">
          {mainCategories.map((category) => {
            const icon = category.icon || SECTION_ICONS[category.id] || '📄';
            const label = category.name;
            const isActive = category.id === currentCategory || category.isActive;
            const isExpanded = expandedCategories.has(category.id);
            const hasSubcategories = category.subcategories && category.subcategories.length > 0;
            const progress = category.screensTotal > 0
              ? Math.round((category.screensCompleted / category.screensTotal) * 100)
              : 0;
            const isComplete = progress === 100;

            return (
              <li key={category.id} className="mb-1">
                {/* Category Header */}
                <button
                  onClick={() => {
                    if (hasSubcategories) {
                      toggleCategory(category.id);
                    }
                    onCategoryClick?.(category.id);
                  }}
                  disabled={!category.isAccessible}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left
                    transition-all duration-150
                    ${isActive
                      ? 'bg-[#07A2A0] text-white'
                      : 'text-gray-700 hover:bg-gray-100'}
                    ${!category.isAccessible ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  {/* Icon */}
                  <span className={`
                    w-8 h-8 flex items-center justify-center rounded-md text-sm
                    ${isActive ? 'bg-white/20' : 'bg-gray-100'}
                  `}>
                    {icon}
                  </span>

                  {/* Label & Progress */}
                  <div className="flex-1 min-w-0">
                    <span className={`block text-sm font-medium truncate ${isActive ? 'text-white' : 'text-gray-900'}`}>
                      {t(`sidebar.sections.${category.id}`, { defaultValue: label })}
                    </span>
                    {category.screensTotal > 0 && (
                      <span className={`text-xs ${isActive ? 'text-white/70' : 'text-gray-500'}`}>
                        {category.screensCompleted}/{category.screensTotal}
                      </span>
                    )}
                  </div>

                  {/* Expand/Collapse indicator + Status */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isComplete ? (
                      <span className={`text-sm ${isActive ? 'text-white' : 'text-green-500'}`}>✓</span>
                    ) : (
                      <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-white/50' : 'bg-gray-300'}`} />
                    )}
                    {hasSubcategories && (
                      <svg
                        className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''} ${isActive ? 'text-white/70' : 'text-gray-400'}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                </button>

                {/* Dynamic Subcategories - always show for active category, toggle for others */}
                {(isExpanded || isActive) && hasSubcategories && (
                  <ul className="mt-1 ml-11 space-y-0.5 border-l-2 border-gray-200">
                    {category.subcategories.map((subcat) => {
                      const isSubcatActive = currentScreenId === subcat.screenId;
                      return (
                        <li key={subcat.id}>
                          <button
                            onClick={() => onSubcategoryClick?.(subcat.screenId)}
                            className={`
                              w-full text-left pl-3 pr-2 py-1.5 text-sm transition-colors flex items-center gap-2
                              ${isSubcatActive
                                ? 'text-[#07A2A0] font-semibold bg-[#07A2A0]/10 border-l-2 border-[#07A2A0] -ml-[2px]'
                                : subcat.isComplete
                                  ? 'text-gray-700 hover:text-[#07A2A0] hover:bg-gray-50'
                                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}
                            `}
                          >
                            {/* Status indicator */}
                            <span className={`w-4 h-4 flex items-center justify-center flex-shrink-0`}>
                              {isSubcatActive ? (
                                <span className="w-2 h-2 rounded-full bg-[#07A2A0]"></span>
                              ) : subcat.isComplete ? (
                                <svg className="w-3.5 h-3.5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              ) : (
                                <span className="w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                              )}
                            </span>
                            <span className="truncate text-xs">
                              {t(`${subcat.id}.title`, { ns: 'screens', defaultValue: subcat.name })}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-100 px-3 py-3 space-y-1">
        <button className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors">
          <span className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 text-sm">📄</span>
          <span className="text-sm">{t('sidebar.documents')}</span>
        </button>
        <button className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors">
          <span className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 text-sm">❓</span>
          <span className="text-sm">{t('sidebar.support')}</span>
        </button>
        <button className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors">
          <span className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 text-sm">👤</span>
          <span className="text-sm">{t('sidebar.profile')}</span>
        </button>
      </div>
    </aside>
  );
};

// Helper function to build categories from screen data
export function buildCategoriesFromScreens(
  screens: Array<{ screen_id: string; category: string; title?: string; order?: number; sidebar_section?: string }>,
  visitedScreens: string[],
  currentScreenCategory: string,
  currentScreenId?: string
): SidebarCategory[] {
  type Row = { screen_id: string; title: string; order: number };
  const buckets = new Map<string, Row[]>();
  for (const id of SIDEBAR_SECTION_ORDER) {
    buckets.set(id, []);
  }

  for (const screen of screens) {
    const sectionId = screen.sidebar_section || resolveSectionIdForCategory(screen.category || 'other');
    const row: Row = {
      screen_id: screen.screen_id,
      title: screen.title || screen.screen_id,
      order: screen.order ?? 0,
    };
    const list = buckets.get(sectionId);
    if (list) {
      list.push(row);
    } else {
      buckets.get('start')!.push(row);
    }
  }

  const currentSectionId = resolveSectionIdForCategory(currentScreenCategory);
  const categories: SidebarCategory[] = [];

  for (const sec of SIDEBAR_SECTIONS) {
    const bucket = buckets.get(sec.id);
    if (!bucket || bucket.length === 0) continue;

    bucket.sort((a, b) => a.order - b.order);
    const completedCount = bucket.filter((s) => visitedScreens.includes(s.screen_id)).length;
    const subcategories: SubcategoryItem[] = bucket.map((screen) => ({
      id: screen.screen_id,
      name: getScreenDisplayName(screen.screen_id, screen.title),
      screenId: screen.screen_id,
      isComplete: visitedScreens.includes(screen.screen_id),
      isActive: screen.screen_id === currentScreenId,
    }));

    categories.push({
      id: sec.id,
      name: sec.label,
      icon: SECTION_ICONS[sec.id] || '📄',
      subcategories,
      screensTotal: bucket.length,
      screensCompleted: completedCount,
      isActive: sec.id === currentSectionId,
      isAccessible: true,
    });
  }

  return categories;
}

// Helper to clean screen titles for display
function cleanScreenTitle(title: string): string {
  // Remove navigation/category prefixes. Don't strip "Income" when part of "Income by X" or "Income from X"
  let cleaned = title
    .replace(/^(Welcome & Setup|Onboarding|Year & Residency|Consent & Access|My Info|Income(?!\s+by\s)(?!\s+from\s)|Deductions|Foreign Reporting|Non-Resident|Payments|Review & Submit|Documents|Support|Profile)\s*/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  
  // If the title is now empty or too short, use original
  if (!cleaned || cleaned.length < 3) {
    cleaned = title;
  }
  
  // Capitalize first letter of each word for consistency
  cleaned = cleaned.split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
  
  // Shorten very long titles
  if (cleaned.length > 30) {
    cleaned = cleaned.substring(0, 27) + '...';
  }
  
  return cleaned;
}

// Map common screen IDs to friendly names
function getScreenDisplayName(screenId: string, title: string): string {
  const screenNameMap: Record<string, string> = {
    // Onboarding
    'welcome': 'Welcome',
    'what_to_expect': 'What to Expect?',
    'what_you_may_need': 'You may need:',
    'tax_year_selection': 'Tax Year',
    'residency_determination': 'Residency',
    'residency_if_not_sure': 'Residency Check',
    
    // Personal Info
    'personal_details': 'Personal Details',
    'address_details': 'Address',
    'tax_identification': 'Tax ID',
    'disability_status': 'Disability Status',
    'marital_status': 'Marital Status',
    'marital_status_change_2024': 'Marital Changes',
    'spouse_details_shown_if_user_married_or_civil_partnership': 'Spouse Details',
    'my_info_review': 'Review My Info',
    
    // Family
    'dependents': 'Dependents',
    'can_anyone_claim_you_as_dependent': 'Dependent Status',
    
    // Housing
    'property_ownership': 'Property',
    'primary_residence': 'Primary Residence',
    
    // Income
    'income_from_spain': 'Income from Spain',
    'employment_status': 'Employment',
    'employment_income': 'Employment Income',
    'self_employment_status': 'Self-Employment',
    'self_employment_income': 'Self-Employment Income',
    'rental_income': 'Rental Income',
    'investment_income': 'Investments',
    'foreign_income': 'Foreign Income',
    'other_income': 'Other Income',
    'income_review': 'Income Summary',
    
    // Deductions
    'deductions_overview': 'Deductions',
    'deductions_review': 'Deductions Summary',
    
    // Review
    'review_your_tax_summary': 'Tax Summary',
    'final_review': 'Final Review',
  };
  
  return screenNameMap[screenId] || cleanScreenTitle(title);
}

export default Sidebar;
